"use client"

import { useEffect, useState, useRef, useCallback } from "react"
import Link from "next/link"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  ArrowLeft,
  Play,
  Square,
  Mic,
  Loader2,
  AlertCircle,
  ServerOff,
  Plus,
  X,
  CheckCircle,
} from "lucide-react"

// -- Types --------------------------------------------------------------------

interface EngineResult {
  engine_id: string
  name: string
  engine_type: string
  similarity: number
  threshold?: number
  detected: boolean
  pct_windows?: number
}

interface DetectResult {
  engines: EngineResult[]
  num_detected: number
  time_sec: number
  recon_quality?: number
  method?: string
  error?: string
}

interface EnrolledEngine {
  engine_id: string
  name: string
  engine_type: string
}

interface HealthStatus {
  status: string
  model_loaded: boolean
}


// -- Demo clips config --------------------------------------------------------

export const DEMO_CLIPS = [
  {
    id: "A",
    engineId: "smalldiesel",
    label: "Small Diesel",
    subtitle: "Single-cyl generator",
    src: "/monitoring/demo/engine_A.wav",
  },
  {
    id: "C",
    engineId: "waterpump",
    label: "Water Pump",
    subtitle: "Centrifugal pump",
    src: "/monitoring/demo/engine_C.wav",
  },
  {
    id: "D",
    engineId: "chainsaw",
    label: "Chainsaw",
    subtitle: "2-stroke",
    src: "/monitoring/demo/engine_D.wav",
  },
  {
    id: "E",
    engineId: "generator",
    label: "Generator",
    subtitle: "Multi-cyl diesel",
    src: "/monitoring/demo/engine_E.wav",
  },
]

// -- Constants ----------------------------------------------------------------

const SAMPLE_RATE = 16000
const DEMO_POLL_MS = 2000
const ENGINE_SERVER = "https://api.gis.gy"

// -- Workspace URL helper -----------------------------------------------------

const wsParam = (base: string, ws: string) =>
  base + (base.includes("?") ? "&" : "?") + "ws=" + ws

// -- WAV Encoder --------------------------------------------------------------

function encodeWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const numSamples = samples.length
  const buffer = new ArrayBuffer(44 + numSamples * 2)
  const view = new DataView(buffer)

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++)
      view.setUint8(offset + i, str.charCodeAt(i))
  }

  writeStr(0, "RIFF")
  view.setUint32(4, 36 + numSamples * 2, true)
  writeStr(8, "WAVE")
  writeStr(12, "fmt ")
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  writeStr(36, "data")
  view.setUint32(40, numSamples * 2, true)

  let offset = 44
  for (let i = 0; i < numSamples; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]))
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true)
    offset += 2
  }

  return buffer
}

// -- Spectrometer -------------------------------------------------------------

function Spectrometer({
  analyser,
  active,
}: {
  analyser: AnalyserNode | null
  active: boolean
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rafRef = useRef<number>(0)

  useEffect(() => {
    if (!analyser || !active || !canvasRef.current) {
      const canvas = canvasRef.current
      if (canvas) {
        const ctx = canvas.getContext("2d")
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
      return
    }

    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const bufferLength = analyser.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)
    const binWidth = (SAMPLE_RATE / 2) / bufferLength
    const maxBin = Math.min(Math.ceil(500 / binWidth), bufferLength)

    function draw() {
      rafRef.current = requestAnimationFrame(draw)
      analyser!.getByteFrequencyData(dataArray)

      const w = canvas.width
      const h = canvas.height

      ctx!.fillStyle = "hsl(240 6% 10%)"
      ctx!.fillRect(0, 0, w, h)

      const barWidth = w / maxBin
      for (let i = 0; i < maxBin; i++) {
        const value = dataArray[i]
        const barHeight = (value / 255) * h
        const pct = value / 255
        const r = Math.floor(pct > 0.6 ? 255 * ((pct - 0.6) / 0.4) : 0)
        const g = Math.floor(pct > 0.3 ? 200 : 100 * pct)
        const b = Math.floor(pct < 0.5 ? 200 : 200 * (1 - pct))

        ctx!.fillStyle = `rgb(${r},${g},${b})`
        ctx!.fillRect(i * barWidth, h - barHeight, barWidth - 0.5, barHeight)
      }

      ctx!.fillStyle = "hsl(0 0% 50%)"
      ctx!.font = "9px monospace"
      for (const hz of [50, 100, 200, 300, 400, 500]) {
        const x = (hz / binWidth / maxBin) * w
        ctx!.fillText(`${hz}`, x, h - 2)
      }
    }

    draw()
    return () => cancelAnimationFrame(rafRef.current)
  }, [analyser, active])

  return (
    <canvas
      ref={canvasRef}
      width={600}
      height={100}
      className="w-full h-[80px] rounded-md border border-border/50"
    />
  )
}

// -- Engine Similarity Card ---------------------------------------------------

function EngineSimilarityCard({ engine }: { engine: EngineResult }) {
  const pct = Math.round(engine.similarity * 100)
  const threshPct = engine.threshold ? Math.round(engine.threshold * 100) : null
  const barColor = engine.detected
    ? "bg-green-500"
    : pct > 30
      ? "bg-yellow-500"
      : "bg-muted-foreground/30"

  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm font-medium">{engine.name}</span>
          <span className="text-xs text-muted-foreground ml-2">
            {engine.engine_type}
          </span>
        </div>
        <Badge
          variant={engine.detected ? "default" : "secondary"}
          className={
            engine.detected
              ? "bg-green-600 hover:bg-green-600 text-white"
              : ""
          }
        >
          {engine.detected ? "DETECTED" : "OFF"}
        </Badge>
      </div>
      <div className="flex items-center gap-3">
        <div className="relative flex-1 h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${barColor}`}
            style={{ width: `${pct}%` }}
          />
          {threshPct !== null && (
            <div
              className="absolute top-0 h-full w-0.5 bg-red-400"
              style={{ left: `${threshPct}%` }}
              title={`Threshold: ${threshPct}%`}
            />
          )}
        </div>
        <span className="text-xs font-mono w-10 text-right">{pct}%</span>
      </div>
      {threshPct !== null && (
        <p className="text-[10px] text-muted-foreground">Thresh: {threshPct}%</p>
      )}
    </div>
  )
}

// -- Enroll Recorder ----------------------------------------------------------

function EnrollRecorder({ onComplete }: { onComplete: (data: Float32Array, sr: number) => void }) {
  const [recording, setRecording] = useState(false)
  const [countdown, setCountdown] = useState(120)
  const samplesRef = useRef<Float32Array>(new Float32Array(0))
  const idxRef = useRef(0)
  const streamRef = useRef<MediaStream | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
    })
    const ctx = new AudioContext({ sampleRate: 16000 })
    const source = ctx.createMediaStreamSource(stream)
    const processor = ctx.createScriptProcessor(4096, 1, 1)

    const totalSamples = 16000 * 120
    samplesRef.current = new Float32Array(totalSamples)
    idxRef.current = 0

    processor.onaudioprocess = (e) => {
      const input = e.inputBuffer.getChannelData(0)
      const buf = samplesRef.current
      let idx = idxRef.current
      for (let i = 0; i < input.length && idx < totalSamples; i++) {
        buf[idx++] = input[i]
      }
      idxRef.current = idx
      if (idx >= totalSamples) {
        // Auto-stop
        stream.getTracks().forEach(t => t.stop())
        processor.disconnect()
        setRecording(false)
        onComplete(buf, 16000)
      }
    }

    source.connect(processor)
    processor.connect(ctx.destination)
    streamRef.current = stream
    ctxRef.current = ctx
    setRecording(true)
    setCountdown(120)

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0 }
        return prev - 1
      })
    }, 1000)
  }

  return (
    <Button
      size="lg"
      variant={recording ? "destructive" : "default"}
      disabled={recording}
      onClick={startRecording}
      className="w-full gap-2"
    >
      {recording ? (
        <><Loader2 className="h-4 w-4 animate-spin" />Recording... {countdown}s</>
      ) : (
        <><Mic className="h-4 w-4" />Start Recording (2 min)</>
      )}
    </Button>
  )
}

// -- DemoContent Props --------------------------------------------------------

interface DemoContentProps {
  workspace: string
  demoClips: Array<{ id: string; engineId: string; label: string; subtitle: string; src: string }>
  title: string
  subtitle: string
  allowEnrollment?: boolean
  variant?: "showcase" | "testing"
}

// -- Main DemoContent Component -----------------------------------------------

export function DemoContent({ workspace, demoClips, title, subtitle, allowEnrollment = false, variant = "showcase" }: DemoContentProps) {
  // Server health
  const [health, setHealth] = useState<HealthStatus | null>(null)
  const [healthLoading, setHealthLoading] = useState(true)

  // Demo clips
  const [playingClips, setPlayingClips] = useState<Set<string>>(new Set())
  const [loadingClips, setLoadingClips] = useState<Set<string>>(new Set())
  const clipSourcesRef = useRef<Map<string, AudioBufferSourceNode>>(new Map())
  const clipGainsRef = useRef<Map<string, GainNode>>(new Map())
  const audioCtxRef = useRef<AudioContext | null>(null)
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const demoPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollAbortRef = useRef<AbortController | null>(null)
  const [demoDetecting, setDemoDetecting] = useState(false)

  // Results
  const [result, setResult] = useState<DetectResult | null>(null)
  const [enrolledEngines, setEnrolledEngines] = useState<EnrolledEngine[]>([])

  // Live mic mode
  const [micActive, setMicActive] = useState(false)
  const micStreamRef = useRef<MediaStream | null>(null)
  const micCtxRef = useRef<AudioContext | null>(null)
  const micProcessorRef = useRef<ScriptProcessorNode | null>(null)
  const micSamplesRef = useRef<Float32Array[]>([])
  const micActiveRef = useRef(false)

  // Enrollment
  const [showEnroll, setShowEnroll] = useState(false)
  const [enrollName, setEnrollName] = useState("")
  const [enrollType, setEnrollType] = useState("")
  const [enrollMode, setEnrollMode] = useState<"record" | "upload">("record")
  const [enrolling, setEnrolling] = useState(false)
  const [enrollResult, setEnrollResult] = useState<string | null>(null)
  const [customEngines, setCustomEngines] = useState<Array<{id: string, name: string, type: string}>>([])

  // Step-by-step enrollment (testing variant)
  const [enrollStep, setEnrollStep] = useState<"idle" | "recording" | "naming" | "saving">("idle")
  const enrollAudioRef = useRef<{ data: Float32Array; sr: number } | null>(null)

  // localStorage key scoped to workspace
  const storageKey = "mineguard_custom_engines_" + workspace

  // -- Audio Context helper ---------------------------------------------------

  const getAudioCtx = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
      audioCtxRef.current = new AudioContext({ sampleRate: SAMPLE_RATE })
    }
    if (audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume()
    }
    return audioCtxRef.current
  }, [])

  // -- Health check + load enrolled engines on mount --------------------------

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const resp = await fetch(`${ENGINE_SERVER}/health?ws=${workspace}`)
        const data: HealthStatus = await resp.json()
        setHealth(data)
      } catch {
        setHealth({ status: "unavailable", model_loaded: false })
      } finally {
        setHealthLoading(false)
      }
    }

    const loadEngines = async () => {
      try {
        const resp = await fetch(`${ENGINE_SERVER}/engines?ws=${workspace}`)
        if (resp.ok) {
          const data = await resp.json()
          setEnrolledEngines(data.engines || [])
        }
      } catch {
        // server not running
      }
    }

    checkHealth()
    loadEngines()

    // Load custom engines from localStorage
    const saved = localStorage.getItem(storageKey)
    if (saved) setCustomEngines(JSON.parse(saved))
  }, [workspace, storageKey])

  // -- Cleanup on unmount -----------------------------------------------------

  useEffect(() => {
    return () => {
      stopAllClips()
      if (audioCtxRef.current) audioCtxRef.current.close()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // -- Demo clip playback -----------------------------------------------------

  const loadingClipsRef = useRef<Set<string>>(new Set())

  const toggleClip = useCallback(
    async (clip: { id: string; src: string }) => {
      const ctx = getAudioCtx()

      // Use refs as source of truth to avoid stale state from rapid clicks
      if (clipSourcesRef.current.has(clip.id)) {
        // Stop this clip
        const source = clipSourcesRef.current.get(clip.id)
        if (source) {
          try { source.stop() } catch { /* already stopped */ }
        }
        clipSourcesRef.current.delete(clip.id)
        const gain = clipGainsRef.current.get(clip.id)
        if (gain) {
          gain.disconnect()
          clipGainsRef.current.delete(clip.id)
        }
        setPlayingClips((prev) => {
          const next = new Set(prev)
          next.delete(clip.id)
          return next
        })
      } else if (!loadingClipsRef.current.has(clip.id)) {
        // Load and play (guard against double-click race)
        loadingClipsRef.current.add(clip.id)
        setLoadingClips(prev => { const next = new Set(prev); next.add(clip.id); return next })
        try {
          const resp = await fetch(clip.src)
          if (!resp.ok) return // No demo clip available (404)
          const arrayBuf = await resp.arrayBuffer()
          const audioBuffer = await ctx.decodeAudioData(arrayBuf)

          // Ensure shared analyser
          if (!analyserRef.current) {
            const an = ctx.createAnalyser()
            an.fftSize = 2048
            an.smoothingTimeConstant = 0.8
            an.connect(ctx.destination)
            analyserRef.current = an
            setAnalyserNode(an)
          }

          const source = ctx.createBufferSource()
          source.buffer = audioBuffer
          source.loop = true

          const gain = ctx.createGain()
          gain.gain.value = micActiveRef.current ? 0 : 1.0

          source.connect(gain)
          gain.connect(analyserRef.current)

          source.start()
          clipSourcesRef.current.set(clip.id, source)
          clipGainsRef.current.set(clip.id, gain)

          // No onended handler -- loop:true means it never ends naturally,
          // and stop() triggering onended causes race conditions

          setPlayingClips((prev) => {
            const next = new Set(prev)
            next.add(clip.id)
            return next
          })
        } catch {
          // Could not load clip
        } finally {
          loadingClipsRef.current.delete(clip.id)
          setLoadingClips(prev => { const next = new Set(prev); next.delete(clip.id); return next })
        }
      }
    },
    [getAudioCtx]
  )

  const stopAllClips = useCallback(() => {
    clipSourcesRef.current.forEach((source) => {
      try { source.stop() } catch { /* already stopped */ }
    })
    clipSourcesRef.current.clear()
    clipGainsRef.current.forEach((gain) => gain.disconnect())
    clipGainsRef.current.clear()
    if (analyserRef.current) {
      analyserRef.current.disconnect()
      analyserRef.current = null
      setAnalyserNode(null)
    }
    // Stop mic
    if (micProcessorRef.current) { micProcessorRef.current.disconnect(); micProcessorRef.current = null }
    if (micStreamRef.current) { micStreamRef.current.getTracks().forEach(t => t.stop()); micStreamRef.current = null }
    if (micCtxRef.current) { micCtxRef.current.close(); micCtxRef.current = null }
    micSamplesRef.current = []
    micActiveRef.current = false
    setMicActive(false)
    setPlayingClips(new Set())
  }, [])

  // -- Live mic mode ----------------------------------------------------------

  const stopMic = useCallback(() => {
    if (micProcessorRef.current) { micProcessorRef.current.disconnect(); micProcessorRef.current = null }
    if (micStreamRef.current) { micStreamRef.current.getTracks().forEach(t => t.stop()); micStreamRef.current = null }
    if (micCtxRef.current) { micCtxRef.current.close(); micCtxRef.current = null }
    micSamplesRef.current = []
    clipGainsRef.current.forEach(g => { g.gain.value = 1.0 })
    micActiveRef.current = false
    setMicActive(false)
  }, [])

  const toggleMic = useCallback(async () => {
    if (micActive) { stopMic(); return }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false, sampleRate: SAMPLE_RATE },
      })
      micStreamRef.current = stream
      const ctx = new AudioContext({ sampleRate: SAMPLE_RATE })
      micCtxRef.current = ctx
      const source = ctx.createMediaStreamSource(stream)
      const processor = ctx.createScriptProcessor(4096, 1, 1)
      processor.onaudioprocess = (e) => {
        micSamplesRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0)))
      }
      source.connect(processor)
      processor.connect(ctx.destination)
      micProcessorRef.current = processor
      // Mute clip speakers
      clipGainsRef.current.forEach(g => { g.gain.value = 0 })
      micActiveRef.current = true
      setMicActive(true)
    } catch { /* mic permission denied */ }
  }, [micActive, stopMic])

  // -- Demo detection polling -------------------------------------------------

  useEffect(() => {
    // Abort any previous in-flight poll
    if (pollAbortRef.current) {
      pollAbortRef.current.abort()
      pollAbortRef.current = null
    }

    if (playingClips.size > 0 || micActive) {
      const controller = new AbortController()
      pollAbortRef.current = controller

      const poll = async () => {
        if (controller.signal.aborted) return
        setDemoDetecting(true)
        try {
          const activeEngineIds = [...new Set([
            ...demoClips.filter((c) => playingClips.has(c.id)).map((c) => c.engineId),
            ...customEngines.filter((e) => playingClips.has(e.id)).map((e) => e.id),
            ...enrolledEngines.filter((e) => playingClips.has(e.engine_id)).map((e) => e.engine_id),
          ])]

          // If mic is active, drain captured samples and encode as base64 WAV
          let micAudioB64: string | undefined
          const chunks = micSamplesRef.current
          if (chunks.length > 0) {
            micSamplesRef.current = []
            const totalLen = chunks.reduce((a, c) => a + c.length, 0)
            const merged = new Float32Array(totalLen)
            let off = 0
            for (const chunk of chunks) { merged.set(chunk, off); off += chunk.length }
            const wavBuf = encodeWav(merged, SAMPLE_RATE)
            const bytes = new Uint8Array(wavBuf)
            let binary = ""
            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
            micAudioB64 = btoa(binary)
          }

          const resp = await fetch(`${ENGINE_SERVER}/detect-demo`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              engines: activeEngineIds,
              ws: workspace,
              ...(micAudioB64 ? { mic_audio: micAudioB64 } : {}),
            }),
            signal: controller.signal,
          })
          if (resp.ok && !controller.signal.aborted) {
            const data: DetectResult = await resp.json()
            setResult(data)
          }
        } catch {
          // ignore poll errors and aborts
        } finally {
          if (!controller.signal.aborted) {
            setDemoDetecting(false)
          }
        }
      }

      poll()
      demoPollRef.current = setInterval(poll, DEMO_POLL_MS)
    } else {
      // Stop polling and clear results
      if (demoPollRef.current) {
        clearInterval(demoPollRef.current)
        demoPollRef.current = null
      }
      setResult(null)
      setDemoDetecting(false)
    }

    return () => {
      if (pollAbortRef.current) {
        pollAbortRef.current.abort()
        pollAbortRef.current = null
      }
      if (demoPollRef.current) {
        clearInterval(demoPollRef.current)
        demoPollRef.current = null
      }
    }
  }, [playingClips, customEngines, micActive, demoClips, workspace])

  // -- Engine Enrollment ------------------------------------------------------

  const enrollEngine = async (audioData: Float32Array, sampleRate: number) => {
    if (!enrollName.trim()) return

    setEnrolling(true)
    setEnrollResult(null)

    const engineId = enrollName.toLowerCase().replace(/[^a-z0-9]/g, "_")

    // Split: first half enrollment, second half demo
    const mid = Math.floor(audioData.length / 2)
    const enrollHalf = audioData.slice(0, mid)
    const demoHalf = audioData.slice(mid)

    try {
      // Upload enrollment half
      const enrollWav = encodeWav(enrollHalf, sampleRate)
      const enrollResp = await fetch(
        `${ENGINE_SERVER}/enroll-upload?engine_id=${engineId}&name=${encodeURIComponent(enrollName)}&engine_type=${encodeURIComponent(enrollType || "unknown")}&ws=${workspace}`,
        { method: "POST", headers: { "Content-Type": "audio/wav" }, body: enrollWav }
      )
      const enrollData = await enrollResp.json()

      if (enrollData.error) {
        setEnrollResult(`Error: ${enrollData.error}`)
        return
      }

      // Upload demo half
      const demoWav = encodeWav(demoHalf, sampleRate)
      await fetch(
        `${ENGINE_SERVER}/demo-clip-upload?engine_id=${engineId}&ws=${workspace}`,
        { method: "POST", headers: { "Content-Type": "audio/wav" }, body: demoWav }
      )

      // Save to localStorage
      const newEngine = { id: engineId, name: enrollName, type: enrollType || "unknown" }
      const updated = [...customEngines, newEngine]
      setCustomEngines(updated)
      localStorage.setItem(storageKey, JSON.stringify(updated))

      // Refresh enrolled engines list
      const engResp = await fetch(`${ENGINE_SERVER}/engines?ws=${workspace}`)
      const engData = await engResp.json()
      setEnrolledEngines(engData.engines || [])

      setEnrollResult(`Enrolled! NMF threshold: ${enrollData.nmf_info?.threshold || "calibrating..."}`)
      setShowEnroll(false)
      setEnrollName("")
      setEnrollType("")
    } catch (err) {
      setEnrollResult(`Error: ${err instanceof Error ? err.message : "Unknown"}`)
    } finally {
      setEnrolling(false)
    }
  }

  const submitEnrollment = async (name: string) => {
    setEnrollStep("saving")
    const audio = enrollAudioRef.current
    if (!audio) return

    const engineId = name.toLowerCase().replace(/[^a-z0-9]/g, "_")
    const mid = Math.floor(audio.data.length / 2)
    const enrollHalf = audio.data.slice(0, mid)
    const demoHalf = audio.data.slice(mid)

    try {
      const enrollWav = encodeWav(enrollHalf, audio.sr)
      const enrollResp = await fetch(
        `${ENGINE_SERVER}/enroll-upload?engine_id=${engineId}&name=${encodeURIComponent(name)}&engine_type=engine&ws=${workspace}`,
        { method: "POST", headers: { "Content-Type": "audio/wav" }, body: enrollWav }
      )
      const enrollData = await enrollResp.json()
      if (enrollData.error) {
        setEnrollResult(`Error: ${enrollData.error}`)
        setEnrollStep("idle")
        return
      }

      const demoWav = encodeWav(demoHalf, audio.sr)
      await fetch(
        `${ENGINE_SERVER}/demo-clip-upload?engine_id=${engineId}&ws=${workspace}`,
        { method: "POST", headers: { "Content-Type": "audio/wav" }, body: demoWav }
      )

      const newEngine = { id: engineId, name, type: "engine" }
      const updated = [...customEngines, newEngine]
      setCustomEngines(updated)
      localStorage.setItem(storageKey, JSON.stringify(updated))

      const engResp = await fetch(`${ENGINE_SERVER}/engines?ws=${workspace}`)
      setEnrolledEngines((await engResp.json()).engines || [])

      enrollAudioRef.current = null
      setEnrollName("")
      setEnrollStep("idle")
      setEnrollResult(`"${name}" enrolled!`)
    } catch (err) {
      setEnrollResult(`Error: ${err instanceof Error ? err.message : "Unknown"}`)
      setEnrollStep("idle")
    }
  }

  const deleteEngine = async (engineId: string) => {
    await fetch(`${ENGINE_SERVER}/engines/${engineId}?ws=${workspace}`, { method: "DELETE" })
    const updated = customEngines.filter(e => e.id !== engineId)
    setCustomEngines(updated)
    localStorage.setItem(storageKey, JSON.stringify(updated))
    // Refresh
    const engResp = await fetch(`${ENGINE_SERVER}/engines?ws=${workspace}`)
    setEnrolledEngines((await engResp.json()).engines || [])
  }

  // -- Render: Server unavailable ---------------------------------------------

  const serverUnavailable =
    !healthLoading && (!health || health.status === "unavailable")

  if (healthLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">
          {"Connecting to engine server..."}
        </span>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link href="/" className="inline-flex size-8 items-center justify-center rounded-lg hover:bg-muted hover:text-foreground transition-colors">
            <ArrowLeft className="size-4" />
          </Link>
          <div>
            <h1 className="text-xl font-bold tracking-tight">{title}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {subtitle}
            </p>
          </div>
        </div>
        {allowEnrollment && variant !== "testing" && (
          <Button variant="outline" size="sm" onClick={() => setShowEnroll(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            {"Add Engine"}
          </Button>
        )}
      </div>

      {/* Server status banner */}
      {serverUnavailable && (
        <Card className="border-yellow-500/50 bg-yellow-500/5">
          <CardContent className="pt-6 flex items-start gap-3">
            <ServerOff className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium">{"Engine server not running"}</p>
              <p className="text-xs text-muted-foreground">
                {"Start the Python engine server to enable detection:"}
              </p>
              <code className="text-xs bg-muted px-2 py-1 rounded block mt-1">
                cd audio && .venv-cnn/bin/python engine_server.py
              </code>
            </div>
          </CardContent>
        </Card>
      )}

      {/* -- Engine Clips ----------------------------------------------------- */}
      {variant === "testing" ? (
        <div className="space-y-4">
          {/* -- Testing variant: step-by-step enrollment wizard -- */}

          {/* Saving state */}
          {enrollStep === "saving" && (
            <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">{"Enrolling + calibrating NMF..."}</span>
            </div>
          )}

          {/* Naming state */}
          {enrollStep === "naming" && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2 text-green-500">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm font-medium">{"Recording complete!"}</span>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{"Name this engine"}</label>
                <input
                  type="text"
                  placeholder={"e.g., Water Pump, Generator, Chainsaw"}
                  value={enrollName}
                  onChange={(e) => setEnrollName(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  autoFocus
                />
              </div>
              <Button
                onClick={() => submitEnrollment(enrollName)}
                disabled={!enrollName.trim()}
                className="w-full"
              >
                {"Save Engine"}
              </Button>
            </div>
          )}

          {/* 0 engines + idle: first enrollment prompt */}
          {enrollStep === "idle" && customEngines.length === 0 && enrolledEngines.length === 0 && (
            <div className="text-center space-y-6 py-8">
              <div className="space-y-2">
                <h2 className="text-lg font-semibold">{"Record Your First Engine"}</h2>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                  {"Hold your device near a running engine and record 1 minute of audio."}
                </p>
              </div>
              <EnrollRecorder onComplete={(data, sr) => {
                enrollAudioRef.current = { data, sr }
                setEnrollStep("naming")
              }} />
            </div>
          )}

          {/* 1+ engines + idle: full interface */}
          {enrollStep === "idle" && (customEngines.length >= 1 || enrolledEngines.length >= 1) && (
            <div className="space-y-4">
              {enrollResult && <p className="text-sm text-green-500">{enrollResult}</p>}
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {Math.max(customEngines.length, enrolledEngines.length)} engine{Math.max(customEngines.length, enrolledEngines.length) !== 1 ? "s" : ""} enrolled. Tap to play or start live detection.
                </p>
                <Button variant="outline" size="sm" onClick={() => setEnrollStep("recording")} className="gap-1.5">
                  <Plus className="h-3.5 w-3.5" />
                  Add
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Merge: use customEngines if available, otherwise fall back to enrolledEngines from server */}
                {(customEngines.length > 0
                  ? customEngines
                  : enrolledEngines.map(e => ({ id: e.engine_id, name: e.name, type: e.engine_type }))
                ).map((eng) => {
                  const clipObj = { id: eng.id, src: `${ENGINE_SERVER}/demo-clip/${eng.id}?ws=${workspace}` }
                  const isPlaying = playingClips.has(eng.id)
                  return (
                    <div key={eng.id} className="relative">
                      <button
                        onClick={() => toggleClip(clipObj)}
                        disabled={serverUnavailable}
                        className={`w-full text-left rounded-xl border p-4 transition-all active:scale-[0.98] ${
                          isPlaying
                            ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                            : "hover:bg-muted/50"
                        } ${serverUnavailable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-sm font-medium block">{eng.name}</span>
                            <span className="text-xs text-muted-foreground">{eng.type}</span>
                          </div>
                          <div
                            className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
                              isPlaying
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted"
                            }`}
                          >
                            {loadingClips.has(eng.id) ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : isPlaying ? (
                              <Square className="h-4 w-4" />
                            ) : (
                              <Play className="h-4 w-4 ml-0.5" />
                            )}
                          </div>
                        </div>
                        {isPlaying && (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                            </span>
                            <span className="text-[10px] text-primary font-medium">{"Playing"}</span>
                          </div>
                        )}
                      </button>
                      <button
                        onClick={() => deleteEngine(eng.id)}
                        className="absolute top-2 right-2 p-1 rounded-full hover:bg-destructive/20 z-10"
                      >
                        <X className="h-3 w-3 text-muted-foreground" />
                      </button>
                    </div>
                  )
                })}

                {/* Live mic card */}
                <button
                  onClick={toggleMic}
                  className={`text-left rounded-xl border p-4 transition-all active:scale-[0.98] ${
                    micActive
                      ? "border-orange-500 bg-orange-500/5 ring-1 ring-orange-500/30"
                      : "hover:bg-muted/50 border-dashed"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium block">
                        {micActive ? "Stop Detection" : "Start Live Detection"}
                      </span>
                      <span className="text-xs text-muted-foreground">{"Detect engines from ambient audio"}</span>
                    </div>
                    <div
                      className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
                        micActive
                          ? "bg-orange-500 text-white"
                          : "bg-muted"
                      }`}
                    >
                      <Mic className="h-4 w-4" />
                    </div>
                  </div>
                  {micActive && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-500 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
                      </span>
                      <span className="text-[10px] text-orange-500 font-medium">
                        {"Detecting — listening to ambient audio"}
                      </span>
                    </div>
                  )}
                </button>
              </div>

              {/* Spectrometer */}
              {playingClips.size > 0 && (
                <div className="space-y-2">
                  <span className="text-xs text-muted-foreground font-medium">
                    {"Live Spectrum (0-500 Hz)"}
                  </span>
                  <Spectrometer
                    analyser={analyserNode}
                    active={playingClips.size > 0}
                  />
                </div>
              )}

              {/* Analyzing indicator */}
              <div className="h-8 flex items-center justify-center">
                {(playingClips.size > 0 || micActive) && demoDetecting && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {"Analyzing..."}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 2+ engines + recording: adding another engine */}
          {enrollStep === "recording" && (
            <div className="text-center space-y-4 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold">{"Record Another Engine"}</h2>
                <Button variant="ghost" size="sm" onClick={() => setEnrollStep("idle")}>{"Cancel"}</Button>
              </div>
              <EnrollRecorder onComplete={(data, sr) => {
                enrollAudioRef.current = { data, sr }
                setEnrollStep("naming")
              }} />
            </div>
          )}
        </div>
      ) : (
        /* -- Showcase variant: original rendering -- */
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {demoClips.length > 0
              ? "Tap an engine to play its audio. The AI detects which engines are running."
              : "Enroll at least one engine first to use detection."}
          </p>

          {/* Clip cards grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {demoClips.map((clip) => {
              const isPlaying = playingClips.has(clip.id)
              return (
                <button
                  key={clip.id}
                  onClick={() => toggleClip(clip)}
                  disabled={serverUnavailable}
                  className={`text-left rounded-xl border p-4 transition-all active:scale-[0.98] ${
                    isPlaying
                      ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                      : "hover:bg-muted/50"
                  } ${serverUnavailable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium block">
                        {clip.label}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {clip.subtitle}
                      </span>
                    </div>
                    <div
                      className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
                        isPlaying
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                    >
                      {loadingClips.has(clip.id) ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : isPlaying ? (
                        <Square className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4 ml-0.5" />
                      )}
                    </div>
                  </div>
                  {isPlaying && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                      </span>
                      <span className="text-[10px] text-primary font-medium">
                        {"Playing"}
                      </span>
                    </div>
                  )}
                </button>
              )
            })}

            {/* Custom enrolled engines */}
            {allowEnrollment && customEngines.map((eng) => {
              const clipObj = { id: eng.id, src: `${ENGINE_SERVER}/demo-clip/${eng.id}?ws=${workspace}` }
              const isPlaying = playingClips.has(eng.id)
              return (
                <div key={eng.id} className="relative">
                  <button
                    onClick={() => toggleClip(clipObj)}
                    disabled={serverUnavailable}
                    className={`w-full text-left rounded-xl border p-4 transition-all active:scale-[0.98] ${
                      isPlaying
                        ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                        : "hover:bg-muted/50"
                    } ${serverUnavailable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium block">{eng.name}</span>
                        <span className="text-xs text-muted-foreground">{eng.type}</span>
                      </div>
                      <div
                        className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
                          isPlaying
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted"
                        }`}
                      >
                        {isPlaying ? (
                          <Square className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4 ml-0.5" />
                        )}
                      </div>
                    </div>
                    {isPlaying && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                        </span>
                        <span className="text-[10px] text-primary font-medium">{"Playing"}</span>
                      </div>
                    )}
                  </button>
                  <button
                    onClick={() => deleteEngine(eng.id)}
                    className="absolute top-2 right-2 p-1 rounded-full hover:bg-destructive/20 z-10"
                  >
                    <X className="h-3 w-3 text-muted-foreground" />
                  </button>
                </div>
              )
            })}
          </div>

          {/* Spectrometer for clip playback */}
          {playingClips.size > 0 && (
            <div className="space-y-2">
              <span className="text-xs text-muted-foreground font-medium">
                Live Spectrum (0-500 Hz)
              </span>
              <Spectrometer
                analyser={analyserNode}
                active={playingClips.size > 0}
              />
            </div>
          )}

          {/* Demo detecting indicator -- fixed height to prevent layout shift */}
          <div className="h-8 flex items-center justify-center">
            {(playingClips.size > 0 || micActive) && demoDetecting && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Analyzing...
              </div>
            )}
          </div>
        </div>
      )}

      {/* -- Enrollment Modal ------------------------------------------------- */}
      {allowEnrollment && showEnroll && (
        <Card className="border-primary/50">
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">{"Enroll New Engine"}</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowEnroll(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <input
              type="text"
              placeholder={"Engine name (e.g., My Pump)"}
              value={enrollName}
              onChange={(e) => setEnrollName(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />

            <input
              type="text"
              placeholder={"Type (e.g., diesel, pump, 2-stroke)"}
              value={enrollType}
              onChange={(e) => setEnrollType(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />

            {/* Mode toggle */}
            <div className="flex rounded-lg border p-1 bg-muted/30">
              <button
                onClick={() => setEnrollMode("record")}
                className={`flex-1 rounded-md px-3 py-2 text-xs font-medium ${enrollMode === "record" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              >
                <Mic className="h-3 w-3 inline mr-1" />{"Record 2 min"}
              </button>
              <button
                onClick={() => setEnrollMode("upload")}
                className={`flex-1 rounded-md px-3 py-2 text-xs font-medium ${enrollMode === "upload" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              >
                {"Upload WAV"}
              </button>
            </div>

            {enrollMode === "upload" && (
              <input
                type="file"
                accept=".wav,audio/wav"
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  if (!file || !enrollName.trim()) return
                  const arrayBuf = await file.arrayBuffer()
                  const ctx = new AudioContext({ sampleRate: 16000 })
                  const audioBuf = await ctx.decodeAudioData(arrayBuf)
                  const samples = audioBuf.getChannelData(0)
                  await enrollEngine(samples, 16000)
                  ctx.close()
                }}
                className="w-full text-sm"
              />
            )}

            {enrollMode === "record" && (
              <EnrollRecorder onComplete={enrollEngine} />
            )}

            {enrolling && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {"Enrolling + calibrating NMF..."}
              </div>
            )}

            {enrollResult && (
              <p className="text-sm text-muted-foreground">{enrollResult}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* -- Results ---------------------------------------------------------- */}
      {result && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">{"Detection Results"}</h2>
            {result.time_sec > 0 && (
              <span className="text-xs text-muted-foreground">
                {result.time_sec.toFixed(2)}s
              </span>
            )}
          </div>

          {result.error && (
            <div className="flex items-center gap-2 text-sm text-red-500 bg-red-500/5 rounded-lg p-3 border border-red-500/20">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {result.error}
            </div>
          )}

          {result.method !== "logreg" && result.recon_quality !== undefined && !result.error && (
            result.recon_quality < 0.35 ? (
              <div className="flex items-start gap-2 text-sm text-yellow-500 bg-yellow-500/5 rounded-lg p-3 border border-yellow-500/20">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Signal quality: {Math.round(result.recon_quality * 100)}% (minimum 35%)</p>
                  <p className="text-xs text-yellow-500/80 mt-0.5">Audio doesn&apos;t match engine patterns. Detection is paused.</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-green-500 bg-green-500/5 rounded-lg p-3 border border-green-500/20">
                <CheckCircle className="h-4 w-4 shrink-0" />
                <p className="font-medium">Signal quality: {Math.round(result.recon_quality * 100)}%</p>
              </div>
            )
          )}

          {result.engines.length > 0 && (
            <>
              <div className="flex items-center gap-2">
                <Badge
                  variant={result.num_detected > 0 ? "default" : "secondary"}
                >
                  {result.num_detected} engine
                  {result.num_detected !== 1 ? "s" : ""} detected
                </Badge>
              </div>
              <div className="grid gap-2">
                {result.engines.map((engine) => (
                  <EngineSimilarityCard
                    key={engine.engine_id}
                    engine={engine}
                  />
                ))}
              </div>
            </>
          )}

          {!result.error && result.engines.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              {"No enrolled engines matched the audio."}
            </p>
          )}
        </div>
      )}

      {/* Enrolled engines list (when no results yet) */}
      {!result && enrolledEngines.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-xs font-medium text-muted-foreground">
            {`Enrolled Engines (${enrolledEngines.length})`}
          </h2>
          <div className="flex flex-wrap gap-2">
            {enrolledEngines.map((e) => (
              <Badge key={e.engine_id} variant="outline" className="text-xs">
                {e.name}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
